import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { prepareChartData, calculateDayStreak } from "../utils";
import { CHART_CONFIG, FETCH_LIMITS } from "../constants";
import type {
	Reflection,
	DailyCheckIn,
	ChartData,
	DashboardStats,
	ChartViewMode,
} from "../types";

interface UseDashboardDataReturn {
	reflections: Reflection[];
	draftReflections: Reflection[];
	recentCheckIns: DailyCheckIn[];
	chartData: ChartData[];
	stats: DashboardStats;
	loading: boolean;
	error: Error | null;
	refetch: () => Promise<void>;
}

export function useDashboardData(
	chartViewMode: ChartViewMode,
): UseDashboardDataReturn {
	const { user } = useAuth();
	const [reflections, setReflections] = useState<Reflection[]>([]);
	const [draftReflections, setDraftReflections] = useState<Reflection[]>([]);
	const [recentCheckIns, setRecentCheckIns] = useState<DailyCheckIn[]>([]);
	const [chartData, setChartData] = useState<ChartData[]>([]);
	const [stats, setStats] = useState<DashboardStats>({
		totalCheckIns: 0,
		totalReflections: 0,
		dayStreak: 0,
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const checkReflectionAnalysis = useCallback(
		async (reflectionIds: string[]): Promise<Record<string, boolean>> => {
			if (!user || reflectionIds.length === 0) return {};

			try {
				const { data, error } = await supabase
					.from("reflection_analysis")
					.select("reflection_id")
					.eq("user_id", user.id)
					.in("reflection_id", reflectionIds);

				if (error) throw error;

				return reflectionIds.reduce(
					(acc, id) => {
						acc[id] =
							data?.some((analysis) => analysis.reflection_id === id) ?? false;
						return acc;
					},
					{} as Record<string, boolean>,
				);
			} catch (err) {
				console.error("Error checking reflection analysis:", err);
				return {};
			}
		},
		[user],
	);

	const fetchDashboardData = useCallback(async () => {
		if (!user) return;

		setLoading(true);
		setError(null);

		try {
			// Fetch reflections with draft support
			const { data: completedData, error: completedError } = await supabase
				.from("reflections")
				.select("*")
				.eq("user_id", user.id)
				.eq("is_draft", false)
				.eq("archived", false)
				.order("created_at", { ascending: false })
				.limit(FETCH_LIMITS.REFLECTIONS);

			let reflectionsData = completedData;
			let draftsData = null;

			if (completedError?.code === "42703") {
				// Fallback if is_draft doesn't exist
				const { data: allData } = await supabase
					.from("reflections")
					.select("*")
					.eq("user_id", user.id)
					.eq("archived", false)
					.order("created_at", { ascending: false })
					.limit(FETCH_LIMITS.REFLECTIONS);
				reflectionsData = allData;
			} else if (completedError) {
				throw completedError;
			} else {
				// Fetch drafts
				const { data: draftData } = await supabase
					.from("reflections")
					.select("*")
					.eq("user_id", user.id)
					.eq("is_draft", true)
					.order("updated_at", { ascending: false })
					.limit(FETCH_LIMITS.DRAFTS);
				draftsData = draftData;
			}

			// Fetch check-ins based on view mode
			let checkInsData = null;

			if (chartViewMode === "days") {
				const startDate = new Date();
				startDate.setDate(startDate.getDate() - CHART_CONFIG.DAYS_WINDOW);

				const { data } = await supabase
					.from("daily_checkins")
					.select("*")
					.eq("user_id", user.id)
					.gte("created_at", startDate.toISOString())
					.order("created_at", { ascending: true });

				checkInsData = data;
			} else {
				const { data } = await supabase
					.from("daily_checkins")
					.select("*")
					.eq("user_id", user.id)
					.order("created_at", { ascending: false })
					.limit(CHART_CONFIG.ENTRIES_LIMIT);

				checkInsData = data?.reverse();
			}

			// Fetch recent check-ins
			const { data: recentCheckInsData } = await supabase
				.from("daily_checkins")
				.select("*")
				.eq("user_id", user.id)
				.order("created_at", { ascending: false })
				.limit(FETCH_LIMITS.RECENT_CHECKINS);

			// Fetch total counts
			const { data: allReflectionsData } = await supabase
				.from("reflections")
				.select("id")
				.eq("user_id", user.id)
				.eq("is_draft", false)
				.eq("archived", false);

			const { data: allCheckInsData } = await supabase
				.from("daily_checkins")
				.select("*")
				.eq("user_id", user.id)
				.order("created_at", { ascending: false });

			// Check analysis availability
			if (reflectionsData && reflectionsData.length > 0) {
				const analysisAvailability = await checkReflectionAnalysis(
					reflectionsData.map((r) => r.id),
				);

				setReflections(
					reflectionsData.map((reflection) => ({
						...reflection,
						hasAnalysis: analysisAvailability[reflection.id] || false,
					})),
				);
			} else {
				setReflections([]);
			}

			setDraftReflections(draftsData || []);
			setRecentCheckIns(recentCheckInsData || []);

			// Always set chartData, even if empty
			if (checkInsData && checkInsData.length > 0) {
				setChartData(
					prepareChartData(checkInsData, CHART_CONFIG.ROLLING_AVERAGE_WINDOW),
				);
			} else {
				setChartData([]);
			}

			setStats({
				totalCheckIns: allCheckInsData?.length || 0,
				totalReflections: allReflectionsData?.length || 0,
				dayStreak: calculateDayStreak(allCheckInsData || []),
			});
		} catch (err) {
			console.error("Error fetching dashboard data:", err);
			setError(err as Error);

			// Set empty states
			setReflections([]);
			setDraftReflections([]);
			setRecentCheckIns([]);
			setChartData([]);
		} finally {
			setLoading(false);
		}
	}, [user, chartViewMode, checkReflectionAnalysis]);

	useEffect(() => {
		fetchDashboardData();
	}, [fetchDashboardData]);

	return {
		reflections,
		draftReflections,
		recentCheckIns,
		chartData,
		stats,
		loading,
		error,
		refetch: fetchDashboardData,
	};
}
